import React, { useRef, useState } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { downloadDocumentPdf, shareDocumentPdf, openDocumentPdf } from "../../../shared/utils/pdfShare.js";
import { numberToIndianWords } from "../../../shared/utils/numberToWords.js";
import { PURCHASE_TEMPLATES, DEFAULT_PURCHASE_TEMPLATE, getPurchaseTemplateById } from "../templates/registry.js";

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const th = { textAlign: "left", fontSize: "10.5px", color: "#475569", borderBottom: "1px solid #cbd5e1", padding: "5px 4px", fontWeight: 700 };
const td = { fontSize: "11px", borderBottom: "1px solid #e2e8f0", padding: "5px 4px", verticalAlign: "top" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };

// Compact slip (A5): readable sizes, nothing below 11px
const csTh = { textAlign: "left", fontSize: "11.5px", color: "#334155", borderBottom: "1px solid #94a3b8", padding: "5px 4px", fontWeight: 700 };
const csTd = { fontSize: "12px", borderBottom: "1px solid #e2e8f0", padding: "5px 4px", verticalAlign: "top" };
const csNum = { ...csTd, textAlign: "right", whiteSpace: "nowrap" };
const csRow = { display: "flex", justifyContent: "space-between", gap: "0.75rem", fontSize: "12.5px", padding: "2px 0" };

const TEMPLATE_KEY = "purchasePrintTemplate"; // last template picked on this device
const readSavedTemplate = () => {
  try { return localStorage.getItem(TEMPLATE_KEY) || ""; } catch { return ""; }
};

/**
 * A purchase bill as our own record (our letterhead, "PURCHASE BILL", From: vendor) — never laid out
 * like the vendor's tax invoice. Download / WhatsApp share / print as PDF.
 * watermark: show a read-only earlier version (no buttons), e.g. "OLD VERSION".
 */
export function PurchasePrintModal({ isOpen, onClose, purchase, watermark = "" }) {
  const { companies, defaultCompany } = useCompanies();
  const docRef = useRef(null);
  const [busy, setBusy] = useState("");
  const [pickedTemplate, setPickedTemplate] = useState(readSavedTemplate);

  if (!purchase) return null;
  const company = companies.find((c) => c.id === purchase.companyId) || (purchase.companyId ? null : defaultCompany) || null;
  const dateFormat = company?.dateFormat || defaultCompany?.dateFormat || "DD-MM-YYYY";
  const fmt = (d) => (d ? formatAppDate(d, dateFormat) : "");
  const isNonGst = purchase.isNonGst || purchase.billType === "Normal";
  const lines = purchase.lineItems || [];
  const paid = Number(purchase.paid || 0);
  const total = Number(purchase.grandTotal || 0);
  const balance = Number(purchase.balance !== undefined ? purchase.balance : Math.max(0, total - paid));
  const tcsAmount = Number(purchase.tcsAmount || 0);
  const tcsLabel = `TCS${Number(purchase.tcsRate || 0) > 0 ? ` (${purchase.tcsRate}%)` : ""}`;
  const companyName = company?.name || purchase.companyName || "";
  const template = getPurchaseTemplateById(pickedTemplate || company?.purchaseTemplate || DEFAULT_PURCHASE_TEMPLATE);
  const pdfOptions = { format: template.pdfFormat };
  const isCompact = template.id === "compact";
  const fileName = `Purchase_${String(purchase.vendorBillNo || purchase.id).replace(/[^A-Za-z0-9-]+/g, "_")}_${String(purchase.vendorName || "Vendor").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;

  const run = async (kind) => {
    if (!docRef.current) return;
    setBusy(kind);
    try {
      if (kind === "download") await downloadDocumentPdf(docRef.current, fileName, pdfOptions);
      if (kind === "print") await openDocumentPdf(docRef.current, fileName, pdfOptions);
      if (kind === "share") {
        const text = [
          `*${companyName}*`,
          `Purchase bill ${purchase.vendorBillNo || ""} from ${purchase.vendorName || "vendor"} (${fmt(purchase.billDate)})`,
          `Total: ${money(total)}${tcsAmount > 0 ? ` (incl. ${tcsLabel} ${money(tcsAmount)})` : ""}${paid > 0 ? ` • Paid: ${money(paid)} • Balance: ${money(balance)}` : ""}`
        ].join("\n");
        await shareDocumentPdf(docRef.current, fileName, { title: `Purchase bill ${purchase.vendorBillNo || ""}`, text, phone: purchase.vendorPhone || "" }, pdfOptions);
      }
    } catch (err) {
      alert(err.message || "Could not create the PDF.");
    } finally {
      setBusy("");
    }
  };

  const pickTemplate = (id) => {
    setPickedTemplate(id);
    try { localStorage.setItem(TEMPLATE_KEY, id); } catch { /* private mode: not remembered */ }
  };

  const discount = Number(purchase.discountAmount || 0);
  const roundOff = Number(purchase.roundOff || 0);

  /* Compact Slip (A5 half sheet) */
  const compactSlip = (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: "16px" }}>{companyName}</div>
          <div style={{ fontSize: "11.5px", color: "#475569" }}>
            {[company?.addressLine1, company?.city, company?.pincode].filter(Boolean).join(", ")}
            {company?.gstin ? ` • GSTIN ${company.gstin}` : ""}
          </div>
        </div>
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <div style={{ fontWeight: 800, fontSize: "13px", color: "#7c3aed" }}>PURCHASE BILL</div>
          <div style={{ fontSize: "12px" }}>No: <strong>{purchase.vendorBillNo || "—"}</strong></div>
          <div style={{ fontSize: "12px" }}>Date: {fmt(purchase.billDate)}</div>
        </div>
      </div>

      <div style={{ borderTop: "1px dashed #475569", borderBottom: "1px dashed #475569", padding: "5px 0", margin: "6px 0 4px", fontSize: "12px" }}>
        <span style={{ color: "#64748b" }}>From: </span>
        <strong>{purchase.vendorName}</strong>
        {purchase.vendorCity ? `, ${purchase.vendorCity}` : ""}
        {!isNonGst && purchase.vendorGstin ? ` • GSTIN ${purchase.vendorGstin}` : ""}
        {isNonGst ? " • Non-GST bill" : ""}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={csTh}>Item</th>
            <th style={{ ...csTh, textAlign: "right" }}>Qty</th>
            <th style={{ ...csTh, textAlign: "right" }}>Rate</th>
            <th style={{ ...csTh, textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((li, i) => {
            const sub = [li.gstCode ? `HSN ${li.gstCode}` : "", !isNonGst && Number(li.gstPercent) ? `GST ${Number(li.gstPercent)}%` : ""].filter(Boolean).join(" • ");
            return (
              <tr key={i}>
                <td style={csTd}>
                  <div style={{ fontWeight: 600 }}>{li.itemName}</div>
                  {sub && <div style={{ fontSize: "11px", color: "#64748b" }}>{sub}</div>}
                </td>
                <td style={csNum}>{li.qty} {li.unit || ""}</td>
                <td style={csNum}>{money(li.rate)}</td>
                <td style={{ ...csNum, fontWeight: 600 }}>{money(li.lineSubtotal !== undefined ? li.lineSubtotal : Number(li.qty || 0) * Number(li.rate || 0))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ width: "62%", marginLeft: "auto", marginTop: "8px" }}>
        <div style={csRow}><span>Subtotal</span><span>{money(purchase.subtotal)}</span></div>
        {discount > 0 && <div style={csRow}><span>Discount</span><span>− {money(discount)}</span></div>}
        {!isNonGst && (purchase.isInterState
          ? <div style={csRow}><span>IGST</span><span>{money(purchase.igst)}</span></div>
          : <>
              <div style={csRow}><span>CGST</span><span>{money(purchase.cgst)}</span></div>
              <div style={csRow}><span>SGST</span><span>{money(purchase.sgst)}</span></div>
            </>)}
        {tcsAmount > 0 && <div style={csRow}><span>{tcsLabel}</span><span>{money(tcsAmount)}</span></div>}
        {roundOff !== 0 && <div style={csRow}><span>Round off</span><span>{money(roundOff)}</span></div>}
        <div style={{ ...csRow, fontSize: "15px", fontWeight: 800, borderTop: "1.5px solid #0f172a", marginTop: "4px", paddingTop: "5px" }}>
          <span>TOTAL</span><span>{money(total)}</span>
        </div>
      </div>

      <div style={{ fontSize: "12px", fontWeight: 600, marginTop: "6px" }}>
        <span style={{ color: "#64748b", fontWeight: 400 }}>Amount in words: </span>{numberToIndianWords(total)}
      </div>
      {paid > 0 && (
        <div style={{ fontSize: "12px", marginTop: "4px" }}>
          Paid: <strong style={{ color: "#047857" }}>{money(paid)}</strong> • Balance due: <strong>{money(balance)}</strong>
        </div>
      )}
      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "10px", borderTop: "1px dashed #cbd5e1", paddingTop: "4px" }}>
        Purchase record kept by {companyName}. The vendor's own bill is the original tax document.
      </div>
    </>
  );

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={watermark ? `${watermark} • ${purchase.vendorBillNo || ""}` : `🖨️ Purchase bill ${purchase.vendorBillNo || ""}`} size="lg">
      {watermark && (
        <div data-testid="old-version-banner" style={{ marginBottom: "0.5rem", padding: "0.5rem 0.75rem", borderRadius: "var(--radius-md)", background: "rgba(245, 158, 11, 0.15)", border: "1px solid rgba(245, 158, 11, 0.5)", color: "var(--warning, #b45309)", fontWeight: 700, textAlign: "center" }}>
          📜 {watermark} — this is how the bill looked before it was edited
        </div>
      )}

      <div style={{ marginBottom: "0.5rem" }}>
        <label htmlFor="purchase-print-template" style={{ fontSize: "0.75rem", fontWeight: "700", color: "var(--text-dim)", display: "block", marginBottom: "2px" }}>
          TEMPLATE
        </label>
        <select
          id="purchase-print-template"
          value={template.id}
          onChange={(e) => pickTemplate(e.target.value)}
          className="form-control"
          style={{ minHeight: "36px", padding: "0.3rem 2.25rem 0.3rem 0.6rem", fontSize: "0.85rem", width: "auto" }}
        >
          {PURCHASE_TEMPLATES.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Printable purchase record (also used for the PDF); compact previews at A5 width */}
      <div
        ref={docRef}
        data-testid="purchase-doc"
        data-template={template.id}
        style={{ background: "#ffffff", color: "#0f172a", borderRadius: "10px", padding: "0.75rem", maxHeight: "58vh", overflow: "auto", fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", ...(isCompact && { maxWidth: "559px", margin: "0 auto" }) }}
      >
        {isCompact ? compactSlip : (<>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", borderBottom: "2px solid #7c3aed", paddingBottom: "8px", marginBottom: "8px" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: "15px" }}>{companyName}</div>
            <div style={{ fontSize: "10.5px", color: "#475569" }}>
              {[company?.addressLine1, company?.city, company?.pincode].filter(Boolean).join(", ")}
            </div>
            {company?.gstin && <div style={{ fontSize: "10.5px", color: "#7c3aed", fontWeight: 700 }}>GSTIN: {company.gstin}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 800, fontSize: "13px", color: "#7c3aed" }}>PURCHASE BILL</div>
            <div style={{ fontSize: "10.5px", color: "#475569" }}>Vendor bill no: <strong>{purchase.vendorBillNo || "—"}</strong></div>
            <div style={{ fontSize: "10.5px", color: "#475569" }}>Date: {fmt(purchase.billDate)}</div>
            <div style={{ fontSize: "10.5px", color: "#475569" }}>{isNonGst ? "Non-GST bill" : "GST bill"}</div>
          </div>
        </div>

        <div style={{ fontSize: "11.5px", marginBottom: "8px" }}>
          <span style={{ color: "#64748b" }}>From: </span>
          <strong>{purchase.vendorName}</strong>
          {purchase.vendorCity ? `, ${purchase.vendorCity}` : ""}
          {!isNonGst && purchase.vendorGstin ? ` • GSTIN ${purchase.vendorGstin}` : ""}
          {purchase.vendorPhone ? ` • 📞 ${purchase.vendorPhone}` : ""}
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={th}>Item</th>
              <th style={th}>HSN</th>
              <th style={{ ...th, textAlign: "right" }}>Qty</th>
              <th style={{ ...th, textAlign: "right" }}>Rate</th>
              {!isNonGst && <th style={{ ...th, textAlign: "right" }}>GST</th>}
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((li, i) => (
              <tr key={i}>
                <td style={td}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 600 }}>{li.itemName}</td>
                <td style={td}>{li.gstCode || ""}</td>
                <td style={num}>{li.qty} {li.unit || ""}</td>
                <td style={num}>{money(li.rate)}</td>
                {!isNonGst && <td style={num}>{Number(li.gstPercent || 0)}%</td>}
                <td style={num}>{money(li.lineSubtotal !== undefined ? li.lineSubtotal : Number(li.qty || 0) * Number(li.rate || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <table style={{ width: "60%", marginLeft: "auto", marginTop: "8px", borderCollapse: "collapse" }}>
          <tbody>
            <tr><td style={td}>Subtotal</td><td style={num}>{money(purchase.subtotal)}</td></tr>
            {Number(purchase.discountAmount || 0) > 0 && <tr><td style={td}>Discount</td><td style={num}>− {money(purchase.discountAmount)}</td></tr>}
            {!isNonGst && (purchase.isInterState
              ? <tr><td style={td}>IGST</td><td style={num}>{money(purchase.igst)}</td></tr>
              : <>
                  <tr><td style={td}>CGST</td><td style={num}>{money(purchase.cgst)}</td></tr>
                  <tr><td style={td}>SGST</td><td style={num}>{money(purchase.sgst)}</td></tr>
                </>)}
            {tcsAmount > 0 && <tr><td style={td}>{tcsLabel}</td><td style={num}>{money(tcsAmount)}</td></tr>}
            {Number(purchase.roundOff || 0) !== 0 && <tr><td style={td}>Round off</td><td style={num}>{money(purchase.roundOff)}</td></tr>}
            <tr><td style={{ ...td, fontWeight: 800 }}>Total</td><td style={{ ...num, fontWeight: 800 }}>{money(total)}</td></tr>
            <tr><td style={td}>Paid</td><td style={{ ...num, color: "#047857" }}>{money(paid)}</td></tr>
            <tr><td style={{ ...td, fontWeight: 700, borderBottom: "none" }}>Balance due</td><td style={{ ...num, fontWeight: 700, borderBottom: "none" }}>{money(balance)}</td></tr>
          </tbody>
        </table>
        <div style={{ fontSize: "9.5px", color: "#94a3b8", marginTop: "10px" }}>
          Purchase record kept by {companyName}. The vendor's own bill is the original tax document.
        </div>
        </>)}
      </div>

      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {!watermark && (
          <>
            <Button variant="secondary" disabled={!!busy} onClick={() => run("download")}>{busy === "download" ? "⏳ Downloading…" : "⬇️ Download PDF"}</Button>
            <Button variant="primary" disabled={!!busy} onClick={() => run("share")} style={{ background: "#16a34a", borderColor: "#16a34a", color: "#ffffff" }}>
              {busy === "share" ? "⏳ Generating PDF…" : "💬 Share PDF on WhatsApp"}
            </Button>
            <Button variant="primary" disabled={!!busy} onClick={() => run("print")}>{busy === "print" ? "⏳ Opening…" : "🖨️ Print / Save as PDF"}</Button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}

export default PurchasePrintModal;
